import { useState, useEffect } from "react";
import axios from "axios";
import { USER_LOCATION_ENDPOINT } from "../globals/endpoints";
import useLanguageFromLocation from "../globals/utils";
import { useTranslation } from "react-i18next";

const DEFAULT_LOCATION = {
  latitude: 19.070976,
  longitude: 72.859648,
  state: "Maharashtra",
  district: "Mumbai",
  staticLocation: true,
};

const useSharedLocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    getLocation();
  }, []);

  const useDefaultLocation = () => {
    sessionStorage.setItem("userLocation", JSON.stringify(DEFAULT_LOCATION));
    setLocation(DEFAULT_LOCATION);
    setIsLoading(false);
  };

  const fetchUserGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchUserStateDistrict(latitude, longitude);
        },
        (err) => {
          console.error("Geolocation error:", err);
          useDefaultLocation();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000, // 10 seconds
          maximumAge: 0,
        }
      );
    } else {
      console.error("Geolocation is not supported.");
      useDefaultLocation();
    }
  };

  const getLocation = () => {
    const storedLocation = sessionStorage.getItem("userLocation");
    if (storedLocation) {
      const parsedLocation = JSON.parse(storedLocation);
      setLocation(parsedLocation);
      setIsLoading(false);
      // If stored location is static, try to get user's actual location on re-rendering the component
      if (parsedLocation.staticLocation) {
        fetchUserGeolocation();
      }
    } else {
      fetchUserGeolocation();
    }
  };

  const fetchUserStateDistrict = async (lat, lon) => {
    try {
      const response = await axios.get(USER_LOCATION_ENDPOINT, {
        params: {
          lat,
          lon,
          format: "json",
        },
      });

      const address = response.data.address;
      const state = address.state || "State not found";
      const district =
        address.county || address.city_district || "District not found";
      const newLocation = {
        state,
        district,
        latitude: lat,
        longitude: lon,
        staticLocation: false,
      };

      sessionStorage.setItem("userLocation", JSON.stringify(newLocation));

      // Check the boolean flag instead of just checking if i18nextLng exists
      const hasUserSelectedLanguage =
        localStorage.getItem("userSelectedLanguage") === "true";
      if (!hasUserSelectedLanguage) {
        const stateLanguage = useLanguageFromLocation(state);
        i18n.changeLanguage(stateLanguage);
      }

      setLocation(newLocation);
    } catch (err) {
      console.error("Failed to fetch location data", err);
      setError("Failed to fetch location data");
    } finally {
      setIsLoading(false);
    }
  };

  return { location, error, isLoading };
};

export default useSharedLocation;
